import React, { useState, useEffect, useRef } from "react";
import BaseModal from "../../../components/BaseModal";
import { showMainToast } from "utils/showToast";

export default function ExtraServiceFormModal({ show, onClose, mode, userData, onSuccess }) {
  const [errors, setErrors] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (show) {
      setErrors({});
      setPreviewImage(null);
      if (mode === "add") {
        setTimeout(() => clearForm(), 100);
      } else if (mode === "edit" && userData?.image) {
        setPreviewImage(userData.image);
      }
    }
  }, [show, mode]);

  const clearForm = () => {
    document.getElementById("extra-service-form-validation")?.reset();
    setPreviewImage(null);
  };

  const validateForm = () => {
    const name = document.getElementById("es_name").value.trim();
    const category = document.getElementById("es_category").value.trim();
    const image = fileInputRef.current?.files[0];

    let newErrors = {};

    if (!name) {
      newErrors.name = "Service name is required.";
    } else if (name.length < 3) {
      newErrors.name = "Service name must be at least 3 characters.";
    }

    if (!category) {
      newErrors.category = "Category is required.";
    }

    if (mode === "add" && !image) {
      newErrors.image = "Please upload an image.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) setPreviewImage(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showMainToast("Please correct the errors before saving.", "danger");
      return;
    }

    const name           = document.getElementById("es_name").value.trim();
    const category       = document.getElementById("es_category").value.trim();
    const description    = document.getElementById("es_description").value.trim();
    const materialClick  = document.getElementById("es_material_click").value.trim();
    const webUrl         = document.getElementById("es_web_url").value.trim();
    const status         = document.querySelector('select[name="status"]').value;
    const image          = fileInputRef.current?.files[0];

    const formData = new FormData();
    formData.append("action", "ADDUpdate");        // ✅ single action for add + update
    formData.append("name", name);
    formData.append("category", category);
    formData.append("description", description);
    formData.append("material_click", materialClick);
    formData.append("web_url", webUrl);
    formData.append("status", status);
    if (mode === "edit") {
      formData.append("id", userData?.id);         // ✅ presence of id = update, in extraServiceAction
    }
    if (image) {
      formData.append("image", image);
    }

    try {
      const response = await fetch("https://pro.mokshiq.in/extra_service_action", {
        method: "POST",                             // ✅ always POST
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });

      const result = await response.json();
      if (result.status) {
        showMainToast(result.data || "Success!", "success");
        setErrors({});
        if (onSuccess) onSuccess();
      } else {
        showMainToast(result.data || "Something went wrong!", "danger");
      }
    } catch (error) {
      console.error("Error:", error);
      showMainToast("Something went wrong!", "danger");
    }
  };

  const handleClose = () => {
    setErrors({});
    setPreviewImage(null);
    onClose();
  };

  return (
    <BaseModal
      show={show}
      onClose={handleClose}
      title={mode === "edit" ? "Edit Extra Service" : "Add Extra Service"}
      footerButtons={[
        { label: "Cancel", className: "btn-secondary", dismiss: true },
        {
          label: mode === "edit" ? "Update" : "Save",
          className: "btn-primary",
          onClick: handleSave,
        },
      ]}
    >
      <div className="card-body">
        <form className="row g-3 app-form rounded-control" id="extra-service-form-validation">

          {/* Name — API: name */}
          <div className="col-md-6">
            <label className="form-label" htmlFor="es_name">Service Name</label>
            <input
              className={`form-control ${errors.name ? "is-invalid" : ""}`}
              id="es_name"
              name="name"
              type="text"
              defaultValue={userData?.name || ""}
            />
            {errors.name && <div className="text-danger mt-1">{errors.name}</div>}
          </div>

          {/* Category — API: category */}
          <div className="col-md-6">
            <label className="form-label" htmlFor="es_category">Category</label>
            <input
              className={`form-control ${errors.category ? "is-invalid" : ""}`}
              id="es_category"
              name="category"
              type="text"
              defaultValue={userData?.category || ""}
            />
            {errors.category && <div className="text-danger mt-1">{errors.category}</div>}
          </div>

          {/* Description — API: description */}
          <div className="col-md-12">
            <label className="form-label" htmlFor="es_description">Description</label>
            <textarea
              className="form-control"
              id="es_description"
              name="description"
              rows={3}
              defaultValue={userData?.description || ""}
            />
          </div>

          {/* Material Click — API: material_click */}
          <div className="col-md-6">
            <label className="form-label" htmlFor="es_material_click">Material Click</label>
            <input
              className="form-control"
              id="es_material_click"
              name="material_click"
              type="text"
              defaultValue={userData?.material_click || ""}
            />
          </div>

          {/* Web URL — API: web_url */}
          <div className="col-md-6">
            <label className="form-label" htmlFor="es_web_url">Web URL</label>
            <input
              className="form-control"
              id="es_web_url"
              name="web_url"
              type="text"
              defaultValue={userData?.web_url || ""}
            />
          </div>

          {/* Status — API: status */}
          <div className="col-md-6">
            <label className="form-label">Status</label>
            <select
              className="form-select w-100"
              name="status"
              defaultValue={
                userData?.status === false || userData?.status === "False" ? "False" : "True"
              }
            >
              <option value="True">Active</option>
              <option value="False">Inactive</option>
            </select>
          </div>

          {/* Image — API: image */}
          <div className="col-md-12">
            <label className="form-label">
              Image {mode === "edit" && <span className="text-muted">(leave empty to keep existing)</span>}
            </label>
            <input
              className={`form-control ${errors.image ? "is-invalid" : ""}`}
              type="file"
              name="image"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleImageChange}
            />
            {errors.image && <div className="text-danger mt-1">{errors.image}</div>}
            {previewImage && (
              <div className="mt-2">
                <img
                  src={previewImage}
                  alt="Extra Service Preview"
                  style={{
                    maxHeight: "120px",
                    maxWidth: "100%",
                    borderRadius: "6px",
                    border: "1px solid #dee2e6",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
          </div>

        </form>
      </div>
    </BaseModal>
  );
}